import { JsonObject } from 'interfaces/JsonObject';

export function* deepObjectIterator(
    obj: JsonObject,
    deepenIntoArrays = false,
): Generator<[string[], unknown]> {
    for (const [prop, value] of Object.entries(obj)) {
        if (value && typeof value === 'object' && (deepenIntoArrays || !Array.isArray(value))) {
            for (const [p, v] of deepObjectIterator(value)) {
                yield [[prop, ...p], v];
            }
        } else {
            yield [[prop], value];
        }
    }
}

// Usage: getObjectValueByPath({ a: { b: { c: 10 } } }, 'a.b.c') => 10
export const getObjectValueByPath = (obj: JsonObject, path: string) => {
    const pathParts = path.split('.');
    const partsCount = pathParts.length;

    let subObj = obj;

    for (let i = 0; i < partsCount; i++) {
        const pathPart = pathParts[i];

        subObj = subObj[pathPart];

        if (i < partsCount - 1 && !(subObj && typeof subObj === 'object')) {
            return undefined;
        }
    }

    return subObj;
};

// Usage: setObjectValueByPath({}, 'a.b.c', 10) => { a: { b: { c: 10 } } }
export const setObjectValueByPath = (obj: JsonObject, path: string, value: JsonObject) => {
    if (!(obj && typeof obj === 'object')) {
        throw Error('Trying to set a value on an invalid object.');
    }

    const pathParts = path.split('.');
    const partsCount = pathParts.length;

    let subObj = obj;

    for (let i = 0; i < partsCount; i++) {
        const pathPart = pathParts[i];

        if (i < partsCount - 1) {
            let newSubObj = subObj[pathPart];
            if (!(newSubObj && typeof newSubObj === 'object')) {
                newSubObj = {};
                subObj[pathPart] = newSubObj;
            }
            subObj = newSubObj;
        } else {
            subObj[pathPart] = value;
        }
    }
};

// Usage: flatMapToObject({ "a.b.c": 100 }) => { a: { b: { c: 100 } } }
export const flatMapToObject = (flatMap: Record<string, unknown>) => {
    const obj: JsonObject = {};

    Object.entries(flatMap).forEach(([propPath, value]) => {
        const propPathParts = propPath.split('.');
        let curObj = obj;
        propPathParts.forEach((pathPart, depth) => {
            if (depth < propPathParts.length - 1) {
                curObj[pathPart] = curObj[pathPart] ?? {};
                curObj = curObj[pathPart];
            } else {
                curObj[pathPart] = value;
            }
        });
    });

    return obj;
};

// Usage: objectToFlatMap({ a: { b: { c: 100 } } }) => { "a.b.c": 100 }
export const objectToFlatMap = (obj: JsonObject) => {
    const flatMap: Record<string, unknown> = {};

    for (const [propPathParts, value] of deepObjectIterator(obj)) {
        flatMap[propPathParts.join('.')] = value;
    }

    return flatMap;
};

// Usage: objectMerge({ a: { b: { c: 100 }, d: 300 } }, { a: { b: { c: 200 } }, e: 500 }) => { a: { b: { c: 200 }, d: 300 }, e: 500 }
export const objectMerge = <T>(obj: T, other: JsonObject) => {
    const result = { ...obj };

    for (const k in other) {
        const v = other[k];
        if (v && typeof v === 'object') {
            result[k] = objectMerge(result[k], other[k]);
        } else {
            result[k] = other[k];
        }
    }

    return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toFilteredObject = (obj: Record<string, any>, predicate: (value: any) => boolean) => {
    return Object.keys(obj).reduce((acc, key) => {
        if (predicate(obj[key])) {
            acc[key] = obj[key];
        }

        return acc;
    }, {});
};
